import { useState, useEffect } from "react";
import axios from "axios";
import { Trash2, Phone, Mail, Calendar, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data);
    } catch (err) {
      console.error("Failed to fetch leads:", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteLead = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      console.error("Failed to delete lead:", err);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  return (
    <div data-testid="admin-leads-page" className="min-h-screen" style={{ background: '#05121d' }}>
      {/* Header */}
      <div className="border-b border-white/10 glass-dark">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-back-btn"
              onClick={() => navigate("/en")}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-heading text-2xl md:text-3xl tracking-wider text-white">
              LEAD <span className="text-gold">DASHBOARD</span>
            </h1>
          </div>
          <div className="text-sm text-slate-400 font-body">
            {leads.length} {leads.length === 1 ? "lead" : "leads"}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading leads...</div>
        ) : leads.length === 0 ? (
          <div data-testid="no-leads-message" className="text-center py-20">
            <p className="text-slate-400 text-lg font-body">No leads yet</p>
            <p className="text-slate-500 text-sm mt-2 font-body">Leads from the contact form will appear here</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {leads.map((lead) => (
              <div
                key={lead.id}
                data-testid={`lead-card-${lead.id}`}
                className="bg-navy-paper border border-white/5 p-6 transition-all duration-300 hover:border-gold/20"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  {/* Lead Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading text-xl text-white tracking-wide">{lead.name}</h3>
                      {lead.language && (
                        <span className="text-xs px-2 py-0.5 bg-teal/10 text-teal border border-teal/20 uppercase tracking-wider">
                          {lead.language}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-slate-400 font-body">
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-teal transition-colors">
                          <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {lead.phone}
                        </a>
                      )}
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-teal transition-colors">
                          <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {lead.email}
                        </a>
                      )}
                      {lead.preferred_date && (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} /> {lead.preferred_date}
                        </span>
                      )}
                    </div>

                    {/* Vehicle & Service */}
                    <div className="flex flex-wrap gap-3 text-sm">
                      {(lead.vehicle_make || lead.vehicle_model || lead.year) && (
                        <span className="px-3 py-1 bg-navy-dark border border-white/5 text-slate-300 font-body">
                          {[lead.year, lead.vehicle_make, lead.vehicle_model].filter(Boolean).join(" ")}
                        </span>
                      )}
                      {lead.service_needed && (
                        <span className="px-3 py-1 bg-gold/10 border border-gold/20 text-gold font-body">
                          {lead.service_needed}
                        </span>
                      )}
                    </div>

                    {lead.message && (
                      <p className="text-sm text-slate-400 font-body leading-relaxed mt-2">
                        "{lead.message}"
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500 font-body">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </span>
                    <button
                      data-testid={`delete-lead-${lead.id}`}
                      onClick={() => deleteLead(lead.id)}
                      className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-400/10 transition-all"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
